import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { api, inr } from "@/lib/api";
import { Reveal, SectionLabel } from "./Reveal";
import { BookingForm } from "./BookingForm";

const FALLBACK = { packages: [], slots: [], recharge: [] };

export const usePackages = () => {
  const [data, setData] = useState(FALLBACK);
  useEffect(() => {
    api.get("/packages").then((r) => setData({
      packages: Array.isArray(r.data?.packages) ? r.data.packages : [],
      slots: Array.isArray(r.data?.slots) ? r.data.slots : [],
      recharge: Array.isArray(r.data?.recharge) ? r.data.recharge : [],
    })).catch(() => setData(FALLBACK));
  }, []);
  return data;
};

const PackageCard = ({ p, active, onClick, i }) => (
  <motion.button
    data-testid={`package-card-${p.id}`}
    onClick={onClick}
    initial={{ opacity: 0, x: -30 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.6, delay: i * 0.07 }}
    whileTap={{ scale: 0.98 }}
    className={`w-full text-left rounded-[22px] p-5 md:p-6 border-2 transition-colors duration-300 flex items-start justify-between gap-4 ${active ? "bg-[#FFEF00] text-black border-black shadow-[6px_6px_0_#14C3F7]" : "bg-[#131627] border-white/10 hover:border-[#14C3F7]/60"}`}
  >
    <div>
      <p className="font-display font-extrabold text-lg md:text-xl">{p.name}</p>
      <p className={`mt-1 text-sm ${active ? "text-black/75" : "text-slate-400"}`}>{p.desc}</p>
    </div>
    <div className="text-right shrink-0">
      <p className="font-display font-black text-xl md:text-2xl">{inr(p.price)}</p>
      <p className={`font-mono text-[11px] uppercase ${active ? "text-black/70" : "text-slate-400"}`}>{p.unit}</p>
      {active && <Check className="ml-auto mt-2" size={20} />}
    </div>
  </motion.button>
);

export const Packages = ({ data }) => {
  const [pkg, setPkg] = useState(null);
  const selected = data.packages.find((p) => p.id === pkg) || data.packages[0];
  return (
    <section id="book" data-testid="packages-section" className="relative py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <SectionLabel>Book online</SectionLabel>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">Pick a pack.<br /><span className="text-[#14C3F7]">Lock your slot.</span></h2>
        </Reveal>
        <div className="mt-14 grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-5 space-y-4">
            {data.packages.map((p, i) => <PackageCard key={p.id} p={p} i={i} active={selected?.id === p.id} onClick={() => setPkg(p.id)} />)}
            {!data.packages.length && <p className="text-slate-400" data-testid="packages-unavailable">Packages are loading — or call us to book.</p>}
          </div>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <BookingForm pkg={selected} slots={data.slots} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
